import { FunctionComponent } from "react";
import { Button, Collapse, Space, Tag, Typography } from "antd";
import { DislikeFilled, DislikeOutlined, LikeFilled, LikeOutlined } from "@ant-design/icons";
import ReactMarkdown, { Components } from "react-markdown";
import { ChatMessage as ChatMessageModel } from "../stores/useChatbotStore";

const { Text } = Typography;

/**
 * Sous-ensemble de markdown réellement rendu dans une bulle de chat étroite. L'instruction système
 * du back interdit déjà titres, tableaux, liens et blocs de code ; cette whitelist est le filet de
 * sécurité si le modèle en produit malgré tout : on dégrade au lieu d'afficher une mise en forme
 * illisible. Le HTML brut n'est jamais interprété (comportement par défaut de react-markdown).
 */
const MARKDOWN_COMPONENTS: Components = {
    h1: ({ children }) => <strong>{children}</strong>,
    h2: ({ children }) => <strong>{children}</strong>,
    h3: ({ children }) => <strong>{children}</strong>,
    h4: ({ children }) => <strong>{children}</strong>,
    h5: ({ children }) => <strong>{children}</strong>,
    h6: ({ children }) => <strong>{children}</strong>,
    a: ({ children }) => <>{children}</>,
    img: () => null,
};

interface ChatMessageProps {
    message: ChatMessageModel;
    onFeedback: (messageId: string, feedback: "THUMBS_UP" | "THUMBS_DOWN") => void;
}

export const ChatMessage: FunctionComponent<ChatMessageProps> = ({ message, onFeedback }) => {
    const isUser = message.role === "user";
    const hasSources = !isUser && message.sources && message.sources.length > 0;
    // Seules les réponses du modèle contiennent du markdown : la saisie de l'utilisateur et les
    // messages d'erreur figés restent affichés littéralement.
    const isMarkdown = !isUser && !message.error;

    return (
        <div className={`chatbot-message-row ${isUser ? "chatbot-message-row--user" : "chatbot-message-row--assistant"}`}>
            <div
                className={`chatbot-message-bubble ${isUser ? "chatbot-message-bubble--user" : "chatbot-message-bubble--assistant"}${message.error ? " chatbot-message-bubble--error" : ""}`}
            >
                {isMarkdown ? (
                    <div className="chatbot-markdown">
                        <ReactMarkdown components={MARKDOWN_COMPONENTS}>{message.content}</ReactMarkdown>
                    </div>
                ) : (
                    <Text style={isUser ? { color: "#fff" } : undefined}>{message.content}</Text>
                )}
            </div>

            {!isUser && !message.error && (
                <div className="chatbot-message-actions">
                    <Space size={4}>
                        <Button
                            type="text"
                            size="small"
                            aria-label="Cette réponse m'a été utile"
                            aria-pressed={message.feedback === "THUMBS_UP"}
                            disabled={message.feedbackPending}
                            icon={message.feedback === "THUMBS_UP" ? <LikeFilled /> : <LikeOutlined />}
                            className={message.feedback === "THUMBS_UP" ? "chatbot-feedback-btn chatbot-feedback-btn--selected" : "chatbot-feedback-btn"}
                            onClick={() => onFeedback(message.id, "THUMBS_UP")}
                        />
                        <Button
                            type="text"
                            size="small"
                            aria-label="Cette réponse ne m'a pas été utile"
                            aria-pressed={message.feedback === "THUMBS_DOWN"}
                            disabled={message.feedbackPending}
                            icon={message.feedback === "THUMBS_DOWN" ? <DislikeFilled /> : <DislikeOutlined />}
                            className={message.feedback === "THUMBS_DOWN" ? "chatbot-feedback-btn chatbot-feedback-btn--selected" : "chatbot-feedback-btn"}
                            onClick={() => onFeedback(message.id, "THUMBS_DOWN")}
                        />
                    </Space>
                </div>
            )}

            {hasSources && (
                <Collapse
                    ghost
                    size="small"
                    className="chatbot-message-sources"
                    items={[
                        {
                            key: "sources",
                            label: <Text type="secondary" style={{ fontSize: 12 }}>Sources utilisées</Text>,
                            children: (
                                <Space size={[4, 4]} wrap>
                                    {message.sources!.map((source, index) => (
                                        <Tag key={index} bordered={false} color="blue">
                                            {[source.theme, source.sectionTitle].filter(Boolean).join(" · ") || "Source"}
                                        </Tag>
                                    ))}
                                </Space>
                            ),
                        },
                    ]}
                />
            )}
        </div>
    );
};
